import { z } from "zod";

/**
 * Self-service profile editing (TW-3464) — the Settings → Profile form. One schema, used by the
 * web form and the API's `ZodValidationPipe` on `PATCH /api/me/profile`. A user only ever edits
 * their own identity row (owner prisma, self-scoped); club + role are NOT editable here (they are
 * structural — changed by an administrator), so they are absent from the schema.
 *
 * Full-replace semantics: every field is sent on save. `name` and `email` are required; the
 * optional free-text fields normalise blank input to `null` so clearing a field persists as null
 * rather than an empty string.
 */

/** Trim free text and treat blank as "not set" (→ null), capped at `max` characters. */
function nullableText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullable();
}

export const updateProfileSchema = z.object({
  name: z.string().trim().min(1, "A name is required").max(200),
  email: z.string().trim().toLowerCase().email("Enter a valid email address").max(200),
  jobTitle: nullableText(120),
  phone: nullableText(40),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** Attach a freshly-uploaded avatar object (by key) to the user (PUT /api/me/photo). */
export const attachProfilePhotoSchema = z.object({
  key: z.string().trim().min(1).max(500),
});

export type AttachProfilePhotoInput = z.infer<typeof attachProfilePhotoSchema>;

/** The editable profile fields as persisted — returned by PATCH /api/me/profile. */
export interface ProfileDto {
  name: string;
  email: string;
  jobTitle: string | null;
  phone: string | null;
}

/** Result of attaching/removing the avatar: the freshly-signed image URL (or null when removed). */
export interface ProfilePhotoDto {
  /** A short-lived signed URL to the avatar, or null when there is no photo on file. */
  image: string | null;
}
